import React from 'react';
import { Box, Button, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import Carousel from '../../components/blog/blogCarousel';

const PROGRAMS = [
  {
    title: 'Conventional Financing',
    description: 'Flexible mortgage options with competitive pricing following Fannie Mae and Freddie Mac guidelines.',
    path: '/conventional',
  },
  {
    title: 'VA Loans',
    description: 'Financing benefits for eligible service members, Veterans, and surviving spouses backed by the VA.',
    path: '/va',
  },
  {
    title: 'FHA Loans',
    description: 'Flexible qualification standards insured by the Federal Housing Administration for a wide range of buyers.',
    path: '/fha',
  },
  {
    title: 'USDA Loans',
    description: 'Zero down payment options for eligible buyers in approved rural and suburban locations.',
    path: '/usda',
  },
  {
    title: 'Refinancing',
    description: 'Adjust your mortgage to lower payments, change loan terms, or access your home equity.',
    path: '/refinancing',
  },
  {
    title: 'Jumbo Loans',
    description: 'Expanded borrowing power for higher-priced properties that exceed conforming loan limits.',
    path: '/jumbo',
  },
  {
    title: 'First Time Homebuyers',
    description: 'Programs designed to help make your first home purchase more accessible and affordable.',
    path: '/first-time-homebuyers',
  },
  {
    title: 'Low Down Payment',
    description: 'Get into your home sooner with down payment options ranging from 0 to 3.5 percent.',
    path: '/low-down-payment',
  },
  {
    title: 'DSCR Program',
    description: 'Qualify based on your investment property’s rental income rather than personal income.',
    path: '/dscr',
  },
];

export default function LoanProgramsPage() {
  const navigate = useNavigate();

  const images = {
    hero: 'https://res.cloudinary.com/ddhrdxc7d/image/upload/v1762973019/full-width_website_header_-_Loan_Programs_purchase_refinance_tazgvw.jpg',
  };

  return (
    <>
      <LandingPageNav />
      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />

      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflowX: 'hidden',
          backgroundImage: `linear-gradient(rgba(10,10,10,.35), rgba(10,10,10,.45)), url(${images.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: { xs: '20vh', md: '26vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: { xs: 2.25, md: 3 },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, px: { xs: 1.5, md: 3 }, maxWidth: 675 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#fff',
              fontWeight: 800,
              lineHeight: 1.1,
              fontSize: { xs: '1.875rem', md: '2.625rem' },
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}
          >
            Loan Programs
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 400,
              fontStyle: 'italic',
              fontSize: { xs: '0.95rem', md: '1.125rem' },
              mt: 1.125,
              textShadow: '0 2px 6px rgba(0,0,0,0.5)',
            }}
          >
            Find the right fit for you
          </Typography>
        </Box>
      </Box>

      {/* Program Cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 3.75, md: 6 } }}>
        <Typography
          variant="h3"
          component="h1"
          align="center"
          sx={{
            fontWeight: 800,
            mb: 3,
            fontSize: { xs: '1.875rem', md: '2.625rem' },
          }}
        >
          Find the Right Loan Program for You
        </Typography>
        <Typography
          variant="h6"
          align="center"
          sx={{
            color: 'text.secondary',
            maxWidth: 825,
            mx: 'auto',
            mb: 5.25,
            fontWeight: 400,
            lineHeight: 1.7,
            fontSize: { xs: '0.95rem', md: '1.125rem' },
          }}
        >
          Jet HomeLoans is proud to offer a variety of mortgage loan options that are designed to meet the unique needs of today&rsquo;s homebuyers and homeowners. Explore our programs below to see what works best for you and your family, and when you&rsquo;re ready to get started, we&rsquo;re here to help you every step of the way.
        </Typography>

        <Grid container spacing={3}>
          {PROGRAMS.map((program) => (
            <Grid item xs={12} sm={6} md={4} key={program.path}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => navigate(program.path)}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ mb: 1.5, fontSize: { xs: '1.05rem', md: '1.2rem' } }}
                  >
                    {program.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, lineHeight: 1.6 }}
                  >
                    {program.description}
                  </Typography>
                </CardContent>
                <Box sx={{ px: 3, pb: 3 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: '#b89b5e',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', md: '0.825rem' },
                      py: 0.9375,
                      '&:hover': { bgcolor: '#ab9767' },
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Footer />
    </>
  );
}
